import type { ReactNode } from 'react';

export interface ColumnDef<T> {
  key: string;
  label: string;
  defaultVisible?: boolean;
  sortKey?: string;
  filterType?: 'text' | 'date-range' | 'select' | 'number-range' | 'searchable-select';
  filterOptions?: { label: string; value: string }[];
  getValue: (item: T) => string | number | null;
  render?: (item: T) => ReactNode;
  align?: 'left' | 'center' | 'right';
  noFilter?: boolean;
  className?: string;
}

function escapeCSVValue(value: unknown): string {
  if (value === null || value === undefined) return '';
  let str = String(value);
  if (/^[=+\-@\t\r]/.test(str)) str = "'" + str;
  if (str.includes(',') || str.includes('"') || str.includes('\n') || str.includes('\r')) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}

export function exportToCSV<T>(
  items: T[],
  columns: ColumnDef<T>[],
  filename: string,
): void {
  const exportCols = columns.filter((c) => !c.noFilter);
  const header = exportCols.map((c) => escapeCSVValue(c.label)).join(',');
  const rows = items.map((item) =>
    exportCols.map((c) => escapeCSVValue(c.getValue(item))).join(',')
  );
  const csvContent = [header, ...rows].join('\n');
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${filename}_${new Date().toISOString().split('T')[0]}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export function copyToClipboard<T>(
  items: T[],
  columns: ColumnDef<T>[],
): Promise<void> {
  const exportCols = columns.filter((c) => !c.noFilter);
  const header = exportCols.map((c) => c.label).join('\t');
  const rows = items.map((item) =>
    exportCols.map((c) => String(c.getValue(item) ?? '').replace(/[\t\n\r]/g, ' ')).join('\t')
  );
  const text = [header, ...rows].join('\n');
  return navigator.clipboard.writeText(text);
}
